import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, ExternalLink } from 'lucide-react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOUR_KEY = 'ctc_demo_tour';        // 'public' | 'compare' | 'portal' | absent
const DEMO_FLAG = 'ctc_demo';            // '1' while in demo mode (persists for the session)
const SUPPLIER_TOKEN_KEY = 'ctc_supplier_token';
const DEMO_SLUG = 'care-tech-demo-co';
const COMPARE_WITH = 'log-my-care';

const PUBLIC_STEPS = [
  { popover: { title: 'Start with the front end', description: 'This is your public listing - exactly what care providers see when they browse for technology. Let us take a quick tour.' } },
  { element: '[data-testid="supplier-tier-badge"]', popover: { title: 'Your listing status', description: 'Your tier is shown up front. Assurance is earned through Care City - no supplier is marked assured until they have been independently reviewed.' } },
  { element: '[data-testid="supplier-podcast"]', popover: { title: 'Your podcast', description: 'Your podcast episode plays right here on your listing - buyers hear it from you directly.' } },
  { element: '[data-testid="supplier-compare-btn"]', popover: { title: 'Compare side by side', description: 'Buyers can weigh you against alternatives. Let us open a live comparison next.' } },
];

const COMPARE_STEPS = [
  { element: '[data-testid="compare-table"]', popover: { title: 'Side-by-side comparison', description: 'Here you are next to another supplier - buyers compare on categories, features, pricing and more, all in one view.' } },
  { popover: { title: 'The buyer decision tool', description: 'That is how providers shortlist. Now let us walk the supplier journey - from data check to a fully unlocked portal.' } },
];

// --- Portal funnel: 3 phases on the same /portal route, driven by the tour ---
const DATACHECK_STEPS = [
  { popover: { title: 'The supplier journey starts here', description: 'Before a supplier goes live they confirm four quick sections. This is the data check - it keeps the directory accurate and unlocks their subscription quote.' } },
  { element: '[data-testid="supplier-data-check"]', popover: { title: 'Confirm your core details', description: 'Company details, trading status, product portfolio and key contacts - four sections, each confirmed with a date stamp.' } },
  { element: '[data-testid="data-check-progress"]', popover: { title: 'Progress tracker', description: 'Once all four are confirmed we auto-generate a quote. Click Next and we\'ll confirm them for you.' } },
];

const QUOTE_STEPS = [
  { element: '[data-testid="supplier-quote-banner"]', popover: { title: 'Your auto-calculated quote', description: 'The quote builds itself from the data check: an annual subscription, a one-off market-research piece rolled into year one, plus a small fee per extra product.' } },
  { element: '[data-testid="quote-monthly"]', popover: { title: 'One simple monthly price', description: 'Everything rolls into a single monthly figure, VAT included. A real supplier pays by card via Stripe - for this demo we\'ll simulate the payment. Click Next.' } },
];

const DASHBOARD_STEPS = [
  { element: '[data-testid="quote-accepted"]', popover: { title: 'Subscription active', description: 'Payment done - they are now a confirmed supplier with the full portal unlocked.' } },
  { element: '[data-testid="portal-analytics"]', popover: { title: 'Private analytics', description: 'Listing views over time - all-time and the last 30 days. Only the supplier sees this; it is never public.' } },
  { element: '[data-testid="supplier-research-report"]', popover: { title: 'Strategic research', description: 'Their paid market-research piece - positioning, keywords and competitors per product - delivered right in the portal.' } },
  { element: '[data-testid="partner-questionnaire"]', popover: { title: 'Partner programme', description: 'Confirmed suppliers can be invited to the Partner tier - a short questionnaire scopes deeper work and a tailored proposal.' } },
  { element: '[data-testid="portal-request-assurance"]', popover: { title: 'Independent assurance', description: 'And they can apply for independent assurance by Care City - earned, never bought - the strongest trust signal on the platform.' } },
  { element: '[data-testid^="portal-product-unlock-"]', popover: { title: 'Publish extra products', description: 'One primary product is included; each additional product is a one-off £200 + VAT to publish into the directory.' } },
  { popover: { title: 'That is the full journey', description: 'Front end → compare → data check → quote → payment → a fully unlocked supplier portal. Click "Explore the site" to jump out of the demo and browse the live front end - you can search the directory and claim a listing for real.' } },
];

const SEGMENTS = {
  public:  { label: 'Front end', match: (p) => p === `/suppliers/${DEMO_SLUG}`, navPath: `/suppliers/${DEMO_SLUG}`, ready: '[data-testid="public-supplier-page"]', steps: PUBLIC_STEPS, doneText: 'Compare now', nextKey: 'compare' },
  compare: { label: 'Compare',   match: (p) => p === '/compare', navPath: `/compare?slugs=${DEMO_SLUG},${COMPARE_WITH}`, ready: '[data-testid="page-compare"]', steps: COMPARE_STEPS, doneText: 'Start supplier journey', nextKey: 'portal' },
  portal:  { label: 'Supplier journey', match: (p) => p === '/portal', navPath: '/portal', portalFlow: true },
};
const ORDER = ['public', 'compare', 'portal'];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem(SUPPLIER_TOKEN_KEY)}` });

export default function DemoTour() {
  const location = useLocation();
  const navigate = useNavigate();
  const [demoActive, setDemoActive] = useState(() => sessionStorage.getItem(DEMO_FLAG) === '1');
  const cleanupRef = useRef(null);

  // One-time migration: an older build kept the demo flag in localStorage, which made
  // the demo bar stick to the live site forever. Demo mode is now a per-tab session flag,
  // so wipe any legacy localStorage flag on load.
  useEffect(() => {
    localStorage.removeItem('ctc_demo');
    localStorage.removeItem('ctc_demo_tour');
  }, []);

  useEffect(() => { setDemoActive(sessionStorage.getItem(DEMO_FLAG) === '1'); }, [location.pathname]);

  const makeDriver = (steps, doneText, onDone) => driver({
    showProgress: true, allowClose: true, overlayColor: 'rgba(11,41,66,0.7)',
    nextBtnText: 'Next', prevBtnText: 'Back', doneBtnText: doneText,
    steps: steps.map((s) => ({ element: s.element, popover: { ...s.popover, side: s.element ? 'bottom' : 'over', align: 'center' } })),
    onDestroyed: onDone,
  });

  // Generic page-to-page segment (public / compare): navigate to the next segment on finish.
  const runSimpleSegment = useCallback((segKey) => {
    const seg = SEGMENTS[segKey];
    let advanced = false, cancelled = false, suppress = false;
    const d = makeDriver(seg.steps, seg.doneText, () => {
      if (advanced || suppress) return;
      advanced = true;
      if (seg.nextKey) { sessionStorage.setItem(TOUR_KEY, seg.nextKey); navigate(SEGMENTS[seg.nextKey].navPath); }
      else { sessionStorage.removeItem(TOUR_KEY); }
    });
    let waited = 0;
    const poll = setInterval(() => {
      if (cancelled) { clearInterval(poll); return; }
      waited += 300;
      if (document.querySelector(seg.ready) || waited >= 9000) { clearInterval(poll); try { d.drive(); } catch (e) { /* noop */ } }
    }, 300);
    cleanupRef.current = () => { cancelled = true; suppress = true; clearInterval(poll); try { d.destroy(); } catch (e) { /* noop */ } };
  }, [navigate]);

  // Portal funnel: data check → (auto-confirm) → quote → (auto-simulate pay) → dashboard.
  const runPortalFlow = useCallback(() => {
    let cancelled = false;
    let activeDriver = null;
    cleanupRef.current = () => { cancelled = true; try { activeDriver && activeDriver.destroy(); } catch (e) { /* noop */ } };

    const waitFor = (selector, timeout = 9000) => new Promise((resolve) => {
      let waited = 0;
      const t = setInterval(() => {
        if (cancelled) { clearInterval(t); resolve(false); return; }
        waited += 200;
        if (document.querySelector(selector) || waited >= timeout) { clearInterval(t); resolve(!!document.querySelector(selector)); }
      }, 200);
    });

    const drivePhase = (steps, doneText) => new Promise((resolve) => {
      if (cancelled) { resolve(); return; }
      const d = makeDriver(steps, doneText, () => resolve());
      activeDriver = d;
      try { d.drive(); } catch (e) { resolve(); }
    });

    (async () => {
      // Phase A - data check gate (skipped if the supplier is already verified)
      const gate = await waitFor('[data-testid="supplier-data-check"]', 6000);
      if (cancelled) return;
      if (gate) {
        await drivePhase(DATACHECK_STEPS, 'Confirm & get my quote');
        if (cancelled) return;
        for (const k of ['company', 'trading', 'portfolio', 'contacts']) {
          try { await axios.post(`${API}/supplier-verify/section/${k}`, { data: {} }, { headers: authHeaders() }); } catch (e) { /* noop */ }
        }
        window.dispatchEvent(new Event('ctc-demo-refetch'));
      }

      // Phase B - auto-calculated quote (skipped if already paid)
      const hasQuote = await waitFor('[data-testid="supplier-quote-banner"]', 9000);
      if (cancelled) return;
      if (hasQuote && !document.querySelector('[data-testid="quote-accepted"]')) {
        await drivePhase(QUOTE_STEPS, 'Simulate payment');
        if (cancelled) return;
        try { await axios.post(`${API}/supplier-verify/quote/demo-pay`, {}, { headers: authHeaders() }); } catch (e) { /* noop */ }
        window.dispatchEvent(new Event('ctc-demo-refetch'));
      }

      // Phase C - unlocked supplier dashboard
      await waitFor('[data-testid="quote-accepted"]', 9000);
      if (cancelled) return;
      await drivePhase(DASHBOARD_STEPS, 'Explore the site');
      try { activeDriver && activeDriver.destroy(); } catch (e) { /* noop */ }
      if (cancelled) return;
      if (exploreRef.current) exploreRef.current();
      else sessionStorage.removeItem(TOUR_KEY);
    })();
  }, []);

  const runSegment = useCallback((segKey) => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    const seg = SEGMENTS[segKey];
    if (!seg) return;
    if (seg.portalFlow) runPortalFlow();
    else runSimpleSegment(segKey);
  }, [runPortalFlow, runSimpleSegment]);

  // auto-start the segment that matches the tour flag + current route
  useEffect(() => {
    const key = sessionStorage.getItem(TOUR_KEY);
    if (key && SEGMENTS[key] && SEGMENTS[key].match(location.pathname)) runSegment(key);
    return () => { if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const jump = (segKey) => {
    const seg = SEGMENTS[segKey];
    sessionStorage.setItem(TOUR_KEY, segKey);
    if (seg.match(location.pathname)) runSegment(segKey);   // same route → restart now
    else navigate(seg.navPath);                              // different route → effect starts it
  };

  const exitDemo = () => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    ['ctc_demo', 'ctc_demo_tour'].forEach((k) => sessionStorage.removeItem(k));
    localStorage.removeItem('ctc_supplier_token');
    setDemoActive(false);
    navigate('/');
  };

  // Leave the demo but KEEP the site-gate token so the visitor lands on the live,
  // unlocked front end (logged out of the demo account) to browse and claim listings.
  const exploreSite = () => {
    if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
    ['ctc_demo', 'ctc_demo_tour'].forEach((k) => sessionStorage.removeItem(k));
    localStorage.removeItem('ctc_supplier_token');
    setDemoActive(false);
    window.location.assign('/');   // full reload so the public site re-reads the open gate + logged-out state
  };
  const exploreRef = useRef(null);
  exploreRef.current = exploreSite;

  const resetDemo = async () => {
    try {
      const token = localStorage.getItem(SUPPLIER_TOKEN_KEY);
      await axios.post(`${API}/public/demo-reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Demo reset - pristine and ready');
      if (cleanupRef.current) { cleanupRef.current(); cleanupRef.current = null; }
      sessionStorage.setItem('ctc_demo_tour', 'public');
      window.location.assign('/suppliers/care-tech-demo-co');
    } catch {
      toast.error('Could not reset demo data');
    }
  };

  if (!demoActive) return null;

  const activeKey = ORDER.find((k) => SEGMENTS[k].match(location.pathname));
  return createPortal(
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[2147483000] flex items-center gap-1.5 bg-[#0B2942] text-white rounded-full shadow-xl pl-4 pr-1.5 py-1.5" data-testid="demo-bar">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-white/50 mr-1">Demo</span>
      {ORDER.map((k) => (
        <button key={k} onClick={() => jump(k)} data-testid={`demo-jump-${k}`}
          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${activeKey === k ? 'bg-[#00A79D] text-white' : 'text-white/80 hover:bg-white/10'}`}>
          {SEGMENTS[k].label}
        </button>
      ))}
      <button onClick={resetDemo} data-testid="demo-reset" title="Reset demo data"
        className="ml-1 inline-flex items-center gap-1 text-[11px] font-semibold text-white/80 hover:text-white hover:bg-white/10 rounded-full px-2.5 py-1.5 transition-colors">
        <RotateCcw className="w-3 h-3" /> Reset
      </button>
      <button onClick={exploreSite} data-testid="demo-explore-site" title="Leave the demo and explore the live site"
        className="ml-0.5 inline-flex items-center gap-1 text-[11px] font-semibold bg-[#00A79D] text-white hover:bg-[#00938a] rounded-full px-3 py-1.5 transition-colors">
        <ExternalLink className="w-3 h-3" /> Explore site
      </button>
      <button onClick={exitDemo} data-testid="demo-exit" title="Exit demo"
        className="ml-0.5 text-white/50 hover:text-white hover:bg-white/10 rounded-full w-7 h-7 flex items-center justify-center text-sm">✕</button>
    </div>,
    document.body,
  );
}
